import React, { FC, memo, MouseEvent, useCallback } from "react";
import { AuthenticatedAvatarWithRemove } from "../../../../../common/avatar";
import { DocumentSetData } from "../../../../index";
import { getInitialsFromName } from "../../../../../user";
import {
  determineNonErrorStatus,
  Status,
} from "../../../../../common/status/status";
import useActiveDocumentSet from "../../../../hooks/useActiveDocumentSet";
import { canUnassignUser } from "../../../../utils/helperFunctions";
import useUpdateDocumentSet from "../../../../hooks/useUpdateDocumentSet";
import useAssigneeAvatars from "../../../../hooks/useAssigneeAvatars";

type Props = {
  documentSet: DocumentSetData;
};

const AssignedCell: FC<Props> = ({ documentSet }) => {
  const { updateAssigneeAndStatusWithExceptionHandling } =
    useActiveDocumentSet();
  const isAnnotationMode = false;

  const updateDocumentSets = useUpdateDocumentSet();
  const { getAssigneeAvatarUrl } = useAssigneeAvatars();

  const unassignUser = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();
      if (canUnassignUser(documentSet, isAnnotationMode)) {
        const newStatus = determineNonErrorStatus(
          documentSet.status,
          isAnnotationMode ? Status.AnnotationToDo : Status.ToDo,
        );

        updateAssigneeAndStatusWithExceptionHandling(
          documentSet!,
          newStatus,
          undefined,
          false,
        ).then(() => {
          const updatedDocumentSet = {
            ...documentSet,
            assignee: undefined,
            status: newStatus,
          };

          updateDocumentSets(updatedDocumentSet);
        });
      }
    },
    [
      documentSet,
      updateAssigneeAndStatusWithExceptionHandling,
      updateDocumentSets,
      isAnnotationMode,
    ],
  );

  if (documentSet?.assignee?.firstName && documentSet?.assignee?.lastName) {
    const { assignee } = documentSet;

    return (
      <AuthenticatedAvatarWithRemove
        showRemove={canUnassignUser(documentSet, isAnnotationMode)}
        onClick={unassignUser}
        size={32}
        tooltip={`${assignee.firstName} ${assignee.lastName}`}
        avatarUrl={getAssigneeAvatarUrl(assignee?.id)}
        fallback={getInitialsFromName(assignee?.firstName, assignee?.lastName)}
        fontSize={13}
      />
    );
  }

  return <div className="avatar__placeholder" />;
};

export default memo(AssignedCell);
